import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, Mail, MapPin, Star, ArrowRight, CheckCircle, Send, Wrench, Search, Home as HomeIcon, Key, IndianRupee, BedDouble, Building, Users } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from './ui/carousel';
import { toast } from 'sonner';
import api from "../lib/api";
import {
  businessInfo,
  services,
  whyChooseUs,
  testimonials,
  stats,
  founderInfo,
  heroImages,
  serviceImages,
  propertySliderImages
} from '../mock';


const Home = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    propertyType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('buy');
  const [searchQuery, setSearchQuery] = useState('');
  const [buyFilters, setBuyFilters] = useState({
    propertyType: 'full_house',
    bhkType: '',
    propertyStatus: ''
  });
  const [rentFilters, setRentFilters] = useState({
    propertyType: 'full_house',
    bhkType: ''
  });

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    
    if (!formData.phone.trim()) {
      toast.error('Please enter your phone number');
      return;
    }
    
    // Phone validation - at least 10 digits
    const phoneDigits = formData.phone.replace(/\D/g, '');
    if (phoneDigits.length < 10) {
      toast.error('Please enter a valid phone number (at least 10 digits)');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await api.post(`/inquiries`, formData);
      
      if (response.data) {
        toast.success('Thank you! We will contact you soon! 🎉');
        setFormData({ name: '', phone: '', propertyType: '', message: '' });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to submit inquiry. Please try again or call us directly.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCall = () => {
    window.location.href = `tel:${businessInfo.phone}`;
  };

  const handleWhatsApp = () => {
    const url = `https://wa.me/${businessInfo.whatsappNumber}?text=${encodeURIComponent(businessInfo.whatsappMessage)}`;
    window.open(url, '_blank');
  };

  const handleSearch = (type) => {
    const params = new URLSearchParams();
    if (searchQuery.trim()) params.set('search', searchQuery.trim());
    if (type === 'buy') {
      params.set('listingType', 'sale');
      if (buyFilters.bhkType) params.set('bhk', buyFilters.bhkType);
      if (buyFilters.propertyStatus) params.set('status', buyFilters.propertyStatus);
      if (buyFilters.propertyType === 'plot') params.set('property_type', 'Plot');
    } else if (type === 'rent') {
      params.set('listingType', 'rent');
      if (rentFilters.bhkType) params.set('bhk', rentFilters.bhkType);
    }
    navigate(`/properties?${params.toString()}`);
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="fixed top-0 w-full bg-card/90 backdrop-blur-md border-b border-border shadow-xs z-50 transition-all">
        <div className="container mx-auto px-4 py-3.5">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-primary/10 rounded-xl">
                <LucideIcons.Home className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="text-xl font-bold font-heading tracking-tight text-foreground">{businessInfo.name}</h1>
                <p className="text-xs text-muted-foreground italic font-medium">{businessInfo.tagline}</p>
              </div>
            </div>
            <div className="hidden md:flex items-center space-x-6">
              <a href="#services" className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors">Services</a>
              <a href="#about" className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors">About</a>
              <a href="#contact" className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors">Contact</a>
              <Button onClick={() => navigate('/list-your-property')} className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold shadow-sm" data-testid="nav-post-property-btn">
                Post Your Property
              </Button>
              <Button onClick={handleCall} variant="outline" className="border-border text-foreground hover:bg-muted font-medium">
                <Phone className="h-4 w-4 mr-2 text-primary" />
                Call Now
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section - Property Search */}
      <section className="pt-28 pb-20 bg-gradient-hero relative overflow-hidden min-h-[75vh] flex items-center">
        {/* Decorative ambient light */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-primary/10 blur-[120px] rounded-full pointer-events-none"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          {/* Heading */}
          <div className="text-center mb-12 max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-semibold uppercase tracking-wider mb-4 border border-primary/20">
              <Star className="h-3.5 w-3.5 fill-primary" /> #1 Property Dealer in Greater Noida
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold font-heading text-foreground tracking-tight mb-4 leading-tight">
              Greater Noida's Most Trusted <span className="text-primary bg-clip-text text-transparent bg-gradient-to-r from-primary to-blue-600">Property Partner</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto font-normal">
              Buy, Sell, Rent — Zero Brokerage, 100% Transparent Deals
            </p>
          </div>

          {/* Search Card - Glassmorphism & Centered */}
          <div className="max-w-3xl mx-auto">
            <Card className="card-glass shadow-2xl border-border/80 rounded-2xl overflow-hidden backdrop-blur-xl">
              {/* Tabs */}
              <div className="flex border-b border-border/60 bg-muted/40 p-1.5" data-testid="search-tabs">
                {[
                  { id: 'buy', label: 'Buy', icon: HomeIcon },
                  { id: 'rent', label: 'Rent', icon: Key },
                  { id: 'sell', label: 'Sell', icon: IndianRupee }
                ].map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex-1 py-3 px-4 text-center font-semibold font-heading text-sm rounded-xl transition-all relative ${
                        activeTab === tab.id 
                          ? 'text-primary bg-card shadow-sm border border-border/50 font-bold'
                          : 'text-muted-foreground hover:text-foreground hover:bg-card/50'
                      }`}
                      data-testid={`tab-${tab.id}`}
                    >
                      <div className="flex items-center justify-center gap-2">
                        <Icon className={`h-4 w-4 ${activeTab === tab.id ? 'text-primary' : 'text-muted-foreground'}`} />
                        {tab.label}
                      </div>
                    </button>
                  );
                })}
              </div>

              <CardContent className="p-6 md:p-8">
                {/* BUY Tab */}
                {activeTab === 'buy' && (
                  <div className="space-y-5" data-testid="buy-tab-content">
                    {/* Search Row */}
                    <div className="flex flex-col md:flex-row gap-3">
                      <div className="flex items-center gap-2.5 bg-muted/70 border border-border/70 rounded-xl px-4 py-3 md:w-52">
                        <MapPin className="h-4 w-4 text-primary flex-shrink-0" />
                        <span className="text-sm font-semibold text-foreground">Greater Noida</span>
                      </div>
                      <div className="flex-1 relative">
                        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <input
                          type="text"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder="Search societies, sectors or landmarks..."
                          className="w-full pl-11 pr-4 py-3 border border-border bg-card rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-foreground placeholder:text-muted-foreground"
                          onKeyDown={(e) => e.key === 'Enter' && handleSearch('buy')}
                          data-testid="buy-search-input"
                        />
                      </div>
                      <Button
                        onClick={() => handleSearch('buy')}
                        className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-3 text-base rounded-xl shadow-md"
                        data-testid="buy-search-btn"
                      >
                        <Search className="h-4 w-4 mr-2" />
                        Search
                      </Button>
                    </div>

                    {/* Filters Row */}
                    <div className="flex flex-wrap items-center gap-4 pt-2">
                      <div className="flex items-center gap-4 bg-muted/40 px-3 py-1.5 rounded-lg border border-border/40">
                        <label className="flex items-center gap-2 cursor-pointer text-sm font-medium text-foreground">
                          <input
                            type="radio"
                            name="buyPropertyType"
                            value="full_house"
                            checked={buyFilters.propertyType === 'full_house'}
                            onChange={(e) => setBuyFilters({...buyFilters, propertyType: e.target.value})}
                            className="w-4 h-4 text-primary accent-primary"
                          />
                          <span>Full House</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer text-sm font-medium text-foreground">
                          <input
                            type="radio"
                            name="buyPropertyType"
                            value="plot"
                            checked={buyFilters.propertyType === 'plot'}
                            onChange={(e) => setBuyFilters({...buyFilters, propertyType: e.target.value, bhkType: '', propertyStatus: ''})}
                            className="w-4 h-4 text-primary accent-primary"
                          />
                          <span>Land/Plot</span>
                        </label>
                      </div>

                      {buyFilters.propertyType === 'full_house' && (
                        <>
                          <select
                            value={buyFilters.bhkType}
                            onChange={(e) => setBuyFilters({...buyFilters, bhkType: e.target.value})}
                            className="text-sm border border-border rounded-xl px-3.5 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-primary bg-card"
                            data-testid="buy-bhk-filter"
                          >
                            <option value="">BHK Type</option>
                            <option value="1BHK">1 BHK</option>
                            <option value="2BHK">2 BHK</option>
                            <option value="3BHK">3 BHK</option>
                            <option value="4BHK">4 BHK</option>
                            <option value="5BHK">5+ BHK</option>
                          </select>

                          <select
                            value={buyFilters.propertyStatus}
                            onChange={(e) => setBuyFilters({...buyFilters, propertyStatus: e.target.value})}
                            className="text-sm border border-border rounded-xl px-3.5 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-primary bg-card"
                            data-testid="buy-status-filter"
                          >
                            <option value="">Property Status</option>
                            <option value="available">Ready to Move</option>
                            <option value="under_construction">Under Construction</option>
                          </select>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* RENT Tab */}
                {activeTab === 'rent' && (
                  <div className="space-y-6" data-testid="rent-tab-content">
                    <div className="grid grid-cols-2 gap-4 max-w-lg mx-auto">
                      {/* I am the Tenant */}
                      <div
                        className="group card-glass border border-border rounded-2xl p-5 cursor-pointer hover:border-primary/50 hover:shadow-lg transition-all text-center"
                        onClick={() => navigate('/properties?listing_type=rent')}
                        data-testid="rent-tenant-card"
                      >
                        <div className="bg-primary/10 w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform">
                          <Search className="h-6 w-6 text-primary" />
                        </div>
                        <h3 className="text-base font-bold font-heading text-foreground mb-1">I am the Tenant</h3>
                        <p className="text-xs text-muted-foreground mb-2">Browse rental properties</p>
                        <span className="text-xs text-primary font-semibold inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                          Explore <ArrowRight className="h-3 w-3" />
                        </span>
                      </div>

                      {/* I am the Owner */}
                      <div
                        className="group card-glass border border-border rounded-2xl p-5 cursor-pointer hover:border-accent/50 hover:shadow-lg transition-all text-center"
                        onClick={() => navigate('/list-your-property')}
                        data-testid="rent-owner-card"
                      >
                        <div className="bg-accent/20 w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform">
                          <Building className="h-6 w-6 text-accent-foreground" />
                        </div>
                        <h3 className="text-base font-bold font-heading text-foreground mb-1">I am the Owner</h3>
                        <p className="text-xs text-muted-foreground mb-2">List your property FREE</p>
                        <span className="text-xs text-accent-foreground font-semibold inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                          List Now <ArrowRight className="h-3 w-3" />
                        </span>
                      </div>
                    </div>

                    {/* Rent Search */}
                    <div className="flex flex-col md:flex-row gap-3">
                      <div className="flex items-center gap-2.5 bg-muted/70 border border-border/70 rounded-xl px-4 py-3 md:w-52">
                        <MapPin className="h-4 w-4 text-primary flex-shrink-0" />
                        <span className="text-sm font-semibold text-foreground">Greater Noida</span>
                      </div>
                      <div className="flex-1 relative">
                        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <input
                          type="text"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder="Search rental properties by society or sector..."
                          className="w-full pl-11 pr-4 py-3 border border-border bg-card rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-foreground placeholder:text-muted-foreground"
                          onKeyDown={(e) => e.key === 'Enter' && handleSearch('rent')}
                          data-testid="rent-search-input"
                        />
                      </div>
                      <Button
                        onClick={() => handleSearch('rent')}
                        className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-3 text-base rounded-xl shadow-md"
                        data-testid="rent-search-btn"
                      >
                        <Search className="h-4 w-4 mr-2" />
                        Search
                      </Button>
                    </div>

                    {/* Rent Filters */}
                    <div className="flex flex-wrap items-center gap-4">
                      <div className="flex items-center gap-3 bg-muted/40 px-3 py-1.5 rounded-lg border border-border/40">
                        <label className="flex items-center gap-1.5 cursor-pointer text-sm font-medium text-foreground">
                          <input type="radio" name="rentPropType" value="full_house" checked={rentFilters.propertyType === 'full_house'} onChange={(e) => setRentFilters({...rentFilters, propertyType: e.target.value})} className="w-4 h-4 accent-primary" />
                          <span>Full House</span>
                        </label>
                        <label className="flex items-center gap-1.5 cursor-pointer text-sm font-medium text-foreground">
                          <input type="radio" name="rentPropType" value="pg" checked={rentFilters.propertyType === 'pg'} onChange={(e) => setRentFilters({...rentFilters, propertyType: e.target.value})} className="w-4 h-4 accent-primary" />
                          <span>PG/Hostel</span>
                        </label>
                        <label className="flex items-center gap-1.5 cursor-pointer text-sm font-medium text-foreground">
                          <input type="radio" name="rentPropType" value="flatmates" checked={rentFilters.propertyType === 'flatmates'} onChange={(e) => setRentFilters({...rentFilters, propertyType: e.target.value})} className="w-4 h-4 accent-primary" />
                          <span>Flatmates</span>
                        </label>
                      </div>
                      <select
                        value={rentFilters.bhkType}
                        onChange={(e) => setRentFilters({...rentFilters, bhkType: e.target.value})}
                        className="text-sm border border-border rounded-xl px-3.5 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-primary bg-card"
                        data-testid="rent-bhk-filter"
                      >
                        <option value="">BHK Type</option>
                        <option value="1BHK">1 BHK</option>
                        <option value="2BHK">2 BHK</option>
                        <option value="3BHK">3 BHK</option>
                        <option value="4BHK">4 BHK</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* SELL Tab */}
                {activeTab === 'sell' && (
                  <div className="space-y-6" data-testid="sell-tab-content">
                    <div className="text-center py-4">
                      <div className="bg-primary/10 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-primary/20">
                        <IndianRupee className="h-8 w-8 text-primary" />
                      </div>
                      <h3 className="text-2xl font-bold font-heading text-foreground mb-2">Sell Your Property</h3>
                      <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
                        List for FREE. Reach thousands of verified buyers in Greater Noida with zero brokerage.
                      </p>
                      <div className="flex gap-3 justify-center flex-wrap">
                        <Button
                          onClick={() => navigate('/list-your-property?listingType=sale')}
                          className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-6 rounded-xl shadow-md"
                          data-testid="sell-post-btn"
                        >
                          <LucideIcons.PlusCircle className="h-4 w-4 mr-2" />
                          List for Sale
                        </Button>
                        <Button
                          onClick={() => navigate('/list-your-property?listingType=rent')}
                          variant="outline"
                          className="border-primary/30 text-primary hover:bg-primary/10 font-semibold px-6 rounded-xl"
                          data-testid="sell-rent-btn"
                        >
                          <LucideIcons.PlusCircle className="h-4 w-4 mr-2" />
                          List for Rent
                        </Button>
                        <Button
                          onClick={handleWhatsApp}
                          variant="outline"
                          className="border-emerald-500/40 text-emerald-600 hover:bg-emerald-50 font-semibold px-6 rounded-xl"
                          data-testid="sell-whatsapp-btn"
                        >
                          <LucideIcons.MessageCircle className="h-4 w-4 mr-2 text-emerald-600" />
                          Talk to Expert
                        </Button>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-3 pt-4 border-t border-border/60">
                      {[
                        { icon: LucideIcons.Users, label: 'Genuine Buyers' },
                        { icon: LucideIcons.ShieldCheck, label: 'Zero Brokerage' },
                        { icon: LucideIcons.Clock, label: 'Quick Listing' }
                      ].map((item, i) => (
                        <div key={i} className="text-center py-2">
                          <item.icon className="h-5 w-5 text-primary mx-auto mb-1" />
                          <p className="text-xs font-semibold text-muted-foreground">{item.label}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Property Owner CTA */}
          <div className="text-center mt-10 mb-4">
            <p className="text-muted-foreground text-xs uppercase font-semibold tracking-wider mb-3">Are you a Property Owner?</p>
            <Button
              onClick={() => navigate('/list-your-property')}
              variant="outline"
              className="border-2 border-primary text-primary hover:bg-primary/10 font-semibold px-8 py-3 rounded-xl shadow-sm"
              data-testid="hero-post-property-btn"
            >
              Post Free Property Ad
            </Button>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-14 bg-gradient-brand text-primary-foreground shadow-inner">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-4xl md:text-5xl font-extrabold font-heading text-white mb-1.5 tracking-tight">{stat.value}</p>
                <p className="text-sm font-medium text-slate-200">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Property Slider Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-block px-4 py-1.5 bg-primary/10 text-primary border border-primary/20 rounded-full text-xs font-bold uppercase tracking-wider mb-3">
              Our Featured Properties
            </div>
            <h2 className="text-3xl md:text-4xl font-extrabold font-heading text-foreground mb-3">
              Premium Societies in <span className="text-primary">Sector Chi V</span>
            </h2>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
              Explore the best residential properties we deal in - from luxury apartments to premium townships
            </p>
          </div>
          
          <div className="max-w-6xl mx-auto">
            <Carousel
              opts={{
                align: "start",
                loop: true,
              }}
              className="w-full"
            >
              <CarouselContent className="-ml-2 md:-ml-4">
                {propertySliderImages.map((property) => (
                  <CarouselItem key={property.id} className="pl-2 md:pl-4 md:basis-1/2 lg:basis-1/3">
                    <Card className="group overflow-hidden border border-border/80 hover:border-primary/60 card-hover-effect rounded-2xl bg-card shadow-sm">
                      <CardContent className="p-0">
                        <div className="relative overflow-hidden">
                          <img
                            src={property.url}
                            alt={property.title}
                            className="w-full h-72 object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/30 to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-300"></div>
                          <div className="absolute bottom-0 left-0 right-0 p-5 text-white transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                            <div className="inline-block px-3 py-1 bg-primary text-xs font-bold rounded-full mb-2 shadow-sm">
                              {property.type}
                            </div>
                            <h3 className="text-xl font-bold font-heading mb-1 text-white">{property.title}</h3>
                            <p className="text-xs text-slate-200 flex items-center font-medium">
                              <MapPin className="h-3.5 w-3.5 mr-1 text-primary" />
                              {property.location}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <div className="flex items-center justify-center gap-4 mt-10">
                <CarouselPrevious className="relative static translate-y-0 border-2 border-primary text-primary hover:bg-primary hover:text-white transition-colors" />
                <CarouselNext className="relative static translate-y-0 border-2 border-primary text-primary hover:bg-primary hover:text-white transition-colors" />
              </div>
            </Carousel>
          </div>

          <div className="text-center mt-12 flex flex-wrap justify-center gap-4">
            <Button onClick={handleWhatsApp} size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-xl shadow-md">
              <LucideIcons.MessageCircle className="h-5 w-5 mr-2" />
              Inquire About These Properties
            </Button>
            <Button onClick={() => navigate('/properties')} size="lg" variant="outline" className="border-2 border-primary text-primary hover:bg-primary/10 font-semibold rounded-xl">
              View All Properties
              <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
            <Button onClick={() => navigate('/list-your-property')} size="lg" variant="outline" className="border-2 border-accent text-accent-foreground hover:bg-accent/20 font-semibold rounded-xl" data-testid="list-your-property-btn">
              <LucideIcons.PlusCircle className="h-5 w-5 mr-2" />
              List Your Property FREE
            </Button>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-muted/30 border-y border-border/50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-extrabold font-heading text-foreground mb-3">
              Why Choose <span className="text-primary">CHIV Properties</span>?
            </h2>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
              We combine local expertise with professional service to deliver exceptional results
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {whyChooseUs.map((item) => {
              const IconComponent = LucideIcons[item.icon];
              return (
                <Card key={item.id} className="border border-border/70 hover:border-primary/50 card-hover-effect rounded-2xl bg-card shadow-sm">
                  <CardContent className="p-7">
                    <div className="bg-primary/10 w-14 h-14 rounded-2xl flex items-center justify-center mb-5 border border-primary/20">
                      <IconComponent className="h-7 w-7 text-primary" />
                    </div>
                    <h3 className="text-xl font-bold font-heading text-foreground mb-2.5">{item.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{item.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-extrabold font-heading text-foreground mb-3">
              Our <span className="text-primary">Services</span>
            </h2>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
              Complete property solutions from buying to maintenance - all under one roof
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {services.map((service) => {
              const IconComponent = LucideIcons[service.icon];
              return (
                <Card key={service.id} className="group card-hover-effect border border-border/80 hover:border-primary/50 rounded-2xl bg-card shadow-sm">
                  <CardContent className="p-6 text-center">
                    <div className="bg-primary/10 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform border border-primary/20">
                      <IconComponent className="h-8 w-8 text-primary" />
                    </div>
                    <h3 className="text-lg font-bold font-heading text-foreground mb-2">{service.title}</h3>
                    <p className="text-xs text-muted-foreground leading-relaxed">{service.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          
          {/* Service Request CTA */}
          <div className="text-center mt-14">
            <Card className="max-w-3xl mx-auto bg-gradient-brand text-primary-foreground border-none rounded-3xl shadow-xl overflow-hidden">
              <CardContent className="p-8 md:p-10">
                <div className="flex items-center justify-center gap-4 mb-4">
                  <Wrench className="h-8 w-8 text-white" />
                  <LucideIcons.Zap className="h-8 w-8 text-white" />
                  <LucideIcons.Paintbrush className="h-8 w-8 text-white" />
                  <LucideIcons.Hammer className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl md:text-3xl font-extrabold font-heading mb-3 text-white">Need Plumber, Electrician, Painter or Carpenter?</h3>
                <p className="text-slate-200 mb-6 text-base">Submit your problem with photos & get multiple competitive quotes!</p>
                <Button 
                  onClick={() => navigate('/service-request')} 
                  size="lg" 
                  className="bg-white text-primary hover:bg-slate-100 font-bold px-8 py-3 rounded-xl shadow-md"
                >
                  <Send className="h-5 w-5 mr-2 text-primary" />
                  Request Service & Get Quotes
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-muted/20 border-t border-border/50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-extrabold font-heading text-foreground mb-3">
              What Our <span className="text-primary">Clients Say</span>
            </h2>
            <p className="text-base md:text-lg text-muted-foreground">Trust built through satisfied customers</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border border-border/80 card-hover-effect rounded-2xl bg-card shadow-sm">
                <CardContent className="p-6 flex flex-col justify-between h-full">
                  <div>
                    <div className="flex mb-3">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="h-4 w-4 fill-accent text-accent" />
                      ))}
                    </div>
                    <p className="text-muted-foreground mb-5 text-xs italic leading-relaxed">"{testimonial.text}"</p>
                  </div>
                  <div className="border-t border-border/60 pt-4">
                    <p className="font-bold text-sm font-heading text-foreground">{testimonial.name}</p>
                    <p className="text-xs text-muted-foreground">{testimonial.location}</p>
                    <p className="text-xs text-primary font-semibold mt-1">{testimonial.property}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* About Founder */}
      <section id="about" className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="order-2 md:order-1">
              <div className="inline-block px-4 py-1.5 bg-primary/10 text-primary border border-primary/20 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
                Meet Our Founder
              </div>
              <h2 className="text-3xl md:text-4xl font-extrabold font-heading text-foreground mb-3">
                {founderInfo.name}
              </h2>
              <p className="text-base font-semibold text-primary mb-5">{founderInfo.title}</p>
              <p className="text-muted-foreground leading-relaxed mb-6 text-sm">{founderInfo.bio}</p>
              <div className="flex items-center space-x-4">
                <Button onClick={handleCall} className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-6 rounded-xl shadow-md">
                  <Phone className="h-4 w-4 mr-2" />
                  Connect Now
                </Button>
              </div>
            </div>
            <div className="order-1 md:order-2">
              <div className="relative">
                <img
                  src={serviceImages.consultation}
                  alt="Er. Gajender Bhati"
                  className="rounded-3xl shadow-2xl w-full h-[480px] object-cover border border-border"
                />
                <div className="absolute -bottom-6 -right-6 card-glass rounded-2xl shadow-xl p-6 border border-border">
                  <p className="text-3xl font-black font-heading text-primary mb-1">1000+</p>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Happy Clients</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="py-20 bg-gradient-brand text-primary-foreground shadow-2xl">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-extrabold font-heading mb-3 text-white">
                Ready to Buy, Sell or Renovate Your Property?
              </h2>
              <p className="text-base md:text-lg text-slate-200">
                Get expert guidance from Greater Noida's most trusted property partner
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              <Card className="bg-card text-foreground rounded-3xl shadow-2xl border-none">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold font-heading text-foreground mb-6">Contact Us</h3>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <Input
                      type="text"
                      name="name"
                      placeholder="Your Name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="border-border bg-background text-foreground rounded-xl"
                    />
                    <Input
                      type="tel"
                      name="phone"
                      placeholder="Phone Number"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="border-border bg-background text-foreground rounded-xl"
                    />
                    <Input
                      type="text"
                      name="propertyType"
                      placeholder="Property Type (e.g., 2BHK, 3BHK, Commercial)"
                      value={formData.propertyType}
                      onChange={handleInputChange}
                      className="border-border bg-background text-foreground rounded-xl"
                    />
                    <Textarea
                      name="message"
                      placeholder="Your Message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={4}
                      className="border-border bg-background text-foreground rounded-xl"
                    />
                    <Button type="submit" className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold py-3 rounded-xl shadow-md" disabled={isSubmitting}>
                      <Send className="h-4 w-4 mr-2" />
                      {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
              <div className="space-y-6">
                <div className="bg-white/10 backdrop-blur-md rounded-3xl p-6 border border-white/20">
                  <h4 className="font-bold font-heading mb-4 text-lg text-white">Quick Contact</h4>
                  <div className="space-y-4">
                    <Button onClick={handleCall} className="w-full bg-white text-primary hover:bg-slate-100 font-bold rounded-xl" size="lg">
                      <Phone className="h-5 w-5 mr-2 text-primary" />
                      {businessInfo.phone}
                    </Button>
                    <Button onClick={handleWhatsApp} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-xl" size="lg">
                      <LucideIcons.MessageCircle className="h-5 w-5 mr-2" />
                      WhatsApp Us
                    </Button>
                  </div>
                </div>
                <div className="bg-white/10 backdrop-blur-md rounded-3xl p-6 border border-white/20">
                  <h4 className="font-bold font-heading mb-3 text-lg text-white">Visit Us</h4>
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <MapPin className="h-5 w-5 mt-1 flex-shrink-0 text-white" />
                      <p className="text-slate-100 text-sm">{businessInfo.address}</p>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Mail className="h-5 w-5 flex-shrink-0 text-white" />
                      <p className="text-slate-100 text-sm">{businessInfo.email}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-0">
        <div className="w-full h-[400px]">
          <iframe
            src={businessInfo.mapEmbedUrl}
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="CHIV Properties Location"
          ></iframe>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 text-slate-400 py-12 border-t border-slate-900">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-2 bg-primary/20 rounded-xl border border-primary/30">
                  <LucideIcons.Home className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="text-white font-bold font-heading text-lg">{businessInfo.name}</h3>
                  <p className="text-xs text-slate-400 italic">{businessInfo.tagline}</p>
                </div>
              </div>
              <p className="text-xs text-slate-400 leading-relaxed">
                Your trusted partner for all property needs in Greater Noida.
              </p>
            </div>
            <div>
              <h4 className="text-white font-bold font-heading mb-4 text-sm uppercase tracking-wider">Quick Links</h4>
              <ul className="space-y-2.5 text-xs font-medium">
                <li><a href="#services" className="hover:text-primary transition-colors">Services</a></li>
                <li><a href="#about" className="hover:text-primary transition-colors">About Us</a></li>
                <li><a href="#contact" className="hover:text-primary transition-colors">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold font-heading mb-4 text-sm uppercase tracking-wider">Services</h4>
              <ul className="space-y-2.5 text-xs font-medium">
                <li>Property Sale & Purchase</li>
                <li>Rental Services</li>
                <li>Documentation</li>
                <li>Renovation & Maintenance</li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold font-heading mb-4 text-sm uppercase tracking-wider">Contact Info</h4>
              <ul className="space-y-2.5 text-xs font-medium">
                <li className="flex items-center space-x-2">
                  <Phone className="h-4 w-4 text-primary" />
                  <span>{businessInfo.phone}</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Mail className="h-4 w-4 text-primary" />
                  <span>{businessInfo.email}</span>
                </li>
                <li className="flex items-start space-x-2">
                  <MapPin className="h-4 w-4 mt-0.5 text-primary" />
                  <span>{businessInfo.address}</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-900 mt-10 pt-8 text-center text-xs text-slate-500">
            <p>&copy; 2024 {businessInfo.name}. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* WhatsApp Floating Button */}
      <button
        onClick={handleWhatsApp}
        className="fixed bottom-6 right-6 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full p-4 shadow-xl hover:shadow-2xl transition-all duration-300 z-50 animate-bounce"
        aria-label="Contact on WhatsApp"
      >
        <LucideIcons.MessageCircle className="h-6 w-6" />
      </button>

    </div>
  );
};

export default Home;
